/**
 * Profile Story Page
 * Rich-text editor for artist bio and story
 * Uses story store with bold/italic/underline formatting
 *
 * @author Varun Pandey
 */

import React, { useState, useRef, useEffect, useLayoutEffect, useMemo } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import toast from 'react-hot-toast';
import { BookOpen, Bold, Italic, Underline, ArrowLeft, User } from 'lucide-react';
import { cn } from '../../utils/cn';
import { useStoryStore } from '../../stores/storyStore';
import { useArtistDetailsStore } from '@/stores/artistDetailsStore';
import OptimizedImage from '@/components/OptimizedImage';
import { useArtistStory } from '@/api/hooks/useArtistStory';
import { useArtistDisplayName, useArtistProfilePic } from '@/api/hooks/useArtistDisplayName';
import { ArtistStoryRichText } from '@/components/profile/ArtistStoryRichText';
import { TOAST_STORY_PENDING_REVIEW } from '@/constants/profilePendingReview';
import { toastPendingReviewInfo } from '@/utils/profileReviewToast';
import { ProfileCompletionBlocksControl } from '@/components/profile/ProfileCompletionBlocksControl';
import { Loader } from '@/components/ui';
import { Sheet } from '@/components/ui/Sheet';
import { ArtistService } from '@/services/artistService';

type PlanKey = 'free' | 'economy' | 'premium' | 'elite';

const StoryFreePreview: React.FC = () => {
  const { storyHtml, hasStory } = useArtistStory();

  return (
    <div className="p-4 md:p-[1rem] flex flex-col justify-center items-center  w-full border border-dashed border-white/10 rounded-xl">
      <p className="text-[28px] md:text-[32px] font-bold bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text mb-4">
        MY STORY
      </p>
      <ArtistStoryRichText storyHtml={storyHtml} hasStory={hasStory} variant="free" />
    </div>
  );
};

const StoryElitePreview: React.FC = () => {
  const { storyHtml, hasStory } = useArtistStory();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="flex items-center max-sm:flex-col justify-center gap-4 md:gap-20 mb-16 px-4 py-4 w-full border border-dashed border-white/10 rounded-xl">
      <div className="relative aspect-[10/16] h-[500px]  rounded-3xl overflow-hidden bg-[#2C2C2E] flex items-center justify-center">
        {profilePic ? (
          <OptimizedImage
            src={profilePic}
            fill={true}
            unoptimized={true}
            alt={displayName}
            className="object-cover object-top rounded-3xl"
          />
        ) : (
          <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
        )}
      </div>

      <div className=" space-y-4">
        <h2 className="text-[32px] md:text-[42px] font-bold bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text">
          My Story
        </h2>
        <ArtistStoryRichText storyHtml={storyHtml} hasStory={hasStory} variant="split" />

        <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] px-8 py-3 text-lg max-sm:text-sm rounded-xl">
          SEE PRICE & BOOK
        </button>
      </div>
    </div>
  );
};

const StoryEconomyPreview: React.FC = () => {
  const { storyHtml, hasStory } = useArtistStory();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="flex items-center max-sm:flex-col justify-center gap-4 md:gap-20 mb-16 px-4 py-4 w-full border border-dashed border-white/10 rounded-xl">
      <div className="relative aspect-[10/16] h-[500px]  rounded-3xl overflow-hidden bg-[#2C2C2E] flex items-center justify-center">
        {profilePic ? (
          <OptimizedImage
            src={profilePic}
            fill={true}
            unoptimized={true}
            alt={displayName}
            className="object-cover object-top rounded-3xl"
          />
        ) : (
          <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
        )}
      </div>

      <div className=" space-y-4">
        <h2 className="text-[32px] md:text-[42px] font-bold bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text">
          My Story
        </h2>
        <ArtistStoryRichText storyHtml={storyHtml} hasStory={hasStory} variant="split" />

        <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] px-8 py-3 text-lg max-sm:text-sm rounded-xl">
          SEE PRICE & BOOK
        </button>
      </div>
    </div>
  );
};

const StoryPremiumPreview: React.FC = () => {
  const { storyHtml, hasStory } = useArtistStory();
  const displayName = useArtistDisplayName();
  const profilePic = useArtistProfilePic();

  return (
    <div className="flex items-center max-sm:flex-col justify-center gap-4 md:gap-20 mb-16 px-4 py-4 w-full border border-dashed border-white/10 rounded-xl">
      <div className="relative aspect-[10/16] h-[500px]  rounded-3xl overflow-hidden bg-[#2C2C2E] flex items-center justify-center">
        {profilePic ? (
          <OptimizedImage
            src={profilePic}
            fill={true}
            unoptimized={true}
            alt={displayName}
            className="object-cover object-top rounded-3xl"
          />
        ) : (
          <User className="h-[40%] w-[40%] text-[#8E8E93] opacity-50" />
        )}
      </div>

      <div className=" space-y-4">
        <h2 className="text-[32px] md:text-[42px] font-bold bg-gradient-to-r from-[#F16B2E] via-[#EC2179] to-[#EC2179] text-transparent bg-clip-text">
          My Story
        </h2>
        <ArtistStoryRichText storyHtml={storyHtml} hasStory={hasStory} variant="split" />

        <button className="bg-gradient-to-b from-[#EC2179] to-[#F16B2E] px-8 py-3 text-lg max-sm:text-sm rounded-xl">
          SEE PRICE & BOOK
        </button>
      </div>
    </div>
  );
};

const STORY_COMPONENTS: Record<PlanKey, React.ComponentType> = {
  free: StoryFreePreview,
  economy: StoryEconomyPreview,
  premium: StoryPremiumPreview,
  elite: StoryElitePreview,
};

function subscriptionNameToPlanKey(subscriptionName: string | null | undefined): PlanKey | null {
  if (!subscriptionName) return null;
  const n = subscriptionName.toLowerCase().trim();
  if (!n) return null;
  if (n.includes('limited')) return 'free';
  if (n.includes('elite')) return 'elite';
  if (n.includes('premium')) return 'premium';
  if (n.includes('economy')) return 'economy';
  if (n.includes('free')) return 'free';
  return null;
}

export const ProfileStoryPage: React.FC = () => {
  const STORY_MIN_LENGTH = 100;
  const STORY_MAX_LENGTH = 2000;
  const MAX_PLAGIARISM_ATTEMPTS = 3;
  const PLAGIARISM_SCORE_THRESHOLD = 50;
  const STORY_VALIDATION_TOAST_ID = 'profile-story-validation-toast';
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const editorRef = useRef<HTMLDivElement | null>(null);
  const { data, isLoading, saveLoading, error: storeError, ensureFetched, updateStory, setError: setStoreError } = useStoryStore();
  const [content, setContent] = useState('');
  const [error, setError] = useState<string | null>(null); // kept for clearing on input
  const [activeStyles, setActiveStyles] = useState({
    bold: false,
    italic: false,
    underline: false,
  });
  const [isEditing, setIsEditing] = useState(false);
  const [isPlagiarismDrawerOpen, setIsPlagiarismDrawerOpen] = useState(false);
  const [plagiarismScore, setPlagiarismScore] = useState<number | null>(null);
  const [latestTries, setLatestTries] = useState<number | null>(null);

  const planParam = (searchParams.get('plan') ?? '').toLowerCase() as PlanKey | '';
  const artistSubscriptions = useArtistDetailsStore((s) => s.data?.subscriptions);
  const ensureArtistDetailsFetched = useArtistDetailsStore((s) => s.ensureFetched);
  const subscriptionPlan = useMemo(
    () => subscriptionNameToPlanKey(artistSubscriptions?.subscription_name),
    [artistSubscriptions?.subscription_name],
  );
  const plan: PlanKey = (planParam && planParam in STORY_COMPONENTS
    ? planParam
    : subscriptionPlan ?? 'free') as PlanKey;
  const planPreviewLabel = plan === 'free' ? 'LIMITED' : plan;
  const StoryComponent = STORY_COMPONENTS[plan];

  useEffect(() => {
    ensureArtistDetailsFetched().catch(() => undefined);
  }, [ensureArtistDetailsFetched]);

  useEffect(() => {
    ensureFetched();
  }, [ensureFetched]);

  useEffect(() => {
    if (storeError) toast.error(storeError);
  }, [storeError]);

  const getPlainTextLength = (html: string) => {
    if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, '').trim().length;
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent ?? '').trim().length;
  };

  const getPlainTextFromHtml = (html: string) => {
    if (typeof document === 'undefined') return html.replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
    const div = document.createElement('div');
    div.innerHTML = html;
    return (div.textContent ?? '').replace(/\s+/g, ' ').trim();
  };

  const getAttemptsLeft = (tries: number | null | undefined) => {
    const used = Number.isFinite(tries) ? Number(tries) : 0;
    return Math.max(0, MAX_PLAGIARISM_ATTEMPTS - used);
  };

  const getTriesCount = (tries: { status: boolean; count: number } | number | null | undefined) => {
    if (typeof tries === 'number') return tries;
    if (tries && typeof tries.count === 'number') return tries.count;
    return 0;
  };

  const showAttemptsInfoToast = () => {
    const attemptsLeft = getAttemptsLeft(latestTries ?? getTriesCount(data?.tries));
    if (attemptsLeft <= 0) {
      toast.error('Maximum number of allowed attempts reached. Please upgrade your plan to continue updating your content.');
      return;
    }
    toast(`Plagiarism attempts left: ${attemptsLeft}`, { icon: 'ℹ️' });
  };

  const showStoryValidationToast = (html: string) => {
    const storyLength = getPlainTextLength(html);

    if (storyLength < STORY_MIN_LENGTH) {
      toast.error(
        `Please enter at least ${STORY_MIN_LENGTH} characters before saving.`,
        { id: STORY_VALIDATION_TOAST_ID },
      );
      return;
    }

    if (storyLength >= STORY_MAX_LENGTH) {
      toast.error(`You have reached the maximum limit of ${STORY_MAX_LENGTH} characters.`, {
        id: STORY_VALIDATION_TOAST_ID,
      });
      return;
    }

    toast.dismiss(STORY_VALIDATION_TOAST_ID);
  };

  useLayoutEffect(() => {
    if (data?.story !== undefined && data.story !== null) {
      const html = data.story;
      setContent(html);
      if (editorRef.current) {
        editorRef.current.innerHTML = html;
      }
    }
  }, [data, isEditing]);

  useEffect(() => {
    const nextCount = getTriesCount(data?.tries);
    setLatestTries((prev) => Math.max(prev ?? 0, nextCount));
  }, [data?.tries]);

  const checkActiveState = () => {
    if (typeof document === 'undefined') return;
    setActiveStyles({
      bold: document.queryCommandState('bold'),
      italic: document.queryCommandState('italic'),
      underline: document.queryCommandState('underline'),
    });
  };

  const handleCommand = (command: 'bold' | 'italic' | 'underline') => {
    if (typeof document === 'undefined') return;

    // Ensure the editor has focus before executing command
    if (editorRef.current && document.activeElement !== editorRef.current) {
      editorRef.current.focus();
    }

    document.execCommand(command);
    checkActiveState();
    if (editorRef.current) {
      setContent(editorRef.current.innerHTML);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setStoreError(null);
    const storyHtml = editorRef.current?.innerHTML ?? content;
    if (!storyHtml.trim() || storyHtml === '<br>') {
      toast.error('Please add your story');
      editorRef.current?.focus();
      return;
    }

    const storyLength = getPlainTextLength(storyHtml);
    if (storyLength < STORY_MIN_LENGTH || storyLength > STORY_MAX_LENGTH) {
      showStoryValidationToast(storyHtml);
      editorRef.current?.focus();
      return;
    }

    try {
      const storyText = getPlainTextFromHtml(storyHtml);
      const plagiarismChecker = (
        ArtistService as unknown as {
          checkPlagiarism?: (text: string) => Promise<{ tries: number; plagiarism_score: number }>;
        }
      ).checkPlagiarism;

      if (typeof plagiarismChecker === 'function') {
        const plagiarismData = await plagiarismChecker(storyText);
        setLatestTries(plagiarismData.tries);

        if (plagiarismData.tries >= MAX_PLAGIARISM_ATTEMPTS) {
          toast.error('Maximum number of allowed attempts reached. Please upgrade your plan to continue updating your content.');
          return;
        }

        if (plagiarismData.plagiarism_score >= PLAGIARISM_SCORE_THRESHOLD) {
          setPlagiarismScore(plagiarismData.plagiarism_score);
          setIsPlagiarismDrawerOpen(true);
          return;
        }
      }

      await updateStory({ story: storyHtml });
      setIsEditing(false);
    } catch {
      toast.error('Failed to save. Please try again.');
    }
  };

  const hasStoryChanged = useMemo(
    () => data != null && (content ?? '') !== (data.story ?? ''),
    [data, content],
  );

  const hasSavedStoryContent = useMemo(
    () => typeof data?.story === 'string' && data.story.trim().length > 0,
    [data?.story],
  );

  const storyTextLength = useMemo(() => {
    if (!content) return 0;
    return getPlainTextLength(content);
  }, [content]);

  const attemptsLeft = useMemo(
    () => getAttemptsLeft(latestTries ?? getTriesCount(data?.tries)),
    [latestTries, data?.tries],
  );

  return (
    <div className="w-full bg-[#0B0B0D] text-white">
      <div className="w-full mx-auto md:px-4 md:py-6 lg:py-8 pb-16">
        <header className="mb-6 lg:mb-8">
          <div className="mb-5 flex items-center justify-between gap-3">
            <button
              type="button"
              onClick={() => navigate(-1)}
              className="flex min-h-[44px] shrink-0 items-center gap-2 -ml-2 rounded-xl pl-2 pr-3 text-[#8E8E93] transition-colors hover:bg-[#1C1C1E] hover:text-white active:bg-[#2C2C2E] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F33363] focus-visible:ring-offset-2 focus-visible:ring-offset-[#0B0B0D]"
            >
              <ArrowLeft className="h-5 w-5 shrink-0" />
              <span className="text-sm font-medium">Back</span>
            </button>
            <ProfileCompletionBlocksControl />
          </div>
          <div className="flex items-center justify-between gap-3 mb-2 flex-wrap">
            <div className="flex items-center gap-3 min-w-0">
              <div className="flex h-10 w-10 lg:h-12 lg:w-12 shrink-0 items-center justify-center rounded-xl bg-[#1C1C1E] border border-[#3A3A3C]">
                <BookOpen className="h-5 w-5 lg:h-6 lg:w-6 text-[#8E8E93]" />
              </div>
              <div>
                <h1 className="text-2xl lg:text-3xl font-bold text-white tracking-tight uppercase tracking-widest">
                  Story
                </h1>
                <p className="text-[#8E8E93] text-sm lg:text-base mt-0.5">
                  Share your artist journey with clients
                </p>
              </div>
            </div>
            <div className="flex justify-end w-full sm:w-auto flex-shrink-0 gap-2">
              {isEditing ? (
                <>
                  <button
                    type="button"
                    onClick={() => {
                      setIsEditing(false);
                      if (data?.story !== undefined && data.story !== null) {
                        const html = data.story;
                        setContent(html);
                        if (editorRef.current) {
                          editorRef.current.innerHTML = html;
                        }
                      }
                    }}
                    className="inline-flex items-center gap-1.5 rounded-full bg-transparent text-[#E5E5EA] border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-[#1C1C1E] hover:text-white transition-colors text-nowrap"
                  >
                    Cancel
                  </button>
                  <button
                    type="submit"
                    form="story-form"
                    disabled={!hasStoryChanged || saveLoading || isLoading}
                    className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-white/90 hover:text-black transition-colors text-nowrap disabled:opacity-50 disabled:pointer-events-none disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
                  >
                    {saveLoading ? 'Saving…' : 'Save'}
                  </button>
                </>
              ) : (
                <button
                  type="button"
                  onClick={() => {
                    if (data?.pending === true) {
                      toastPendingReviewInfo(TOAST_STORY_PENDING_REVIEW);
                      return;
                    }
                    setIsEditing(true);
                  }}
                  disabled={isLoading}
                  className="inline-flex items-center gap-1.5 rounded-full bg-white text-black border border-[#3A3A3C] px-4 py-2 text-xs font-medium hover:bg-white/90 hover:text-black transition-colors text-nowrap disabled:opacity-50 disabled:pointer-events-none disabled:bg-[#3A3A3C] disabled:text-[#8E8E93]"
                >
                  {hasSavedStoryContent ? 'Update Story' : 'Add Story'}
                </button>
              )}
            </div>
          </div>
        </header>
        {!isEditing && (isLoading || saveLoading) && (
          <div className="flex items-center justify-center">
            <Loader size="small" />
          </div>
        )}
        {!isEditing && !(isLoading || saveLoading) && hasSavedStoryContent && <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4] flex items-center justify-between gap-3">
          <span>
            This is a live preview of how your Story section will appear on your public website.
          </span>
          <span className="inline-flex items-center rounded-full px-3 py-1 text-[11px] font-medium uppercase tracking-wide text-[#E5E5EA]">
            Plan: {planPreviewLabel}
          </span>
        </div>}
        {!isEditing && !(isLoading || saveLoading) && !hasSavedStoryContent && (
          <div className="mb-4 rounded-xl border border-[#2C2C2E] bg-[#141416] px-4 py-3 text-xs text-[#A0A0A4]">
            Your Story is not added yet. Please add it using the <span className="font-medium text-[#E5E5EA]">Add Story</span> button.
          </div>
        )}

        {!isEditing && !(isLoading || saveLoading) && hasSavedStoryContent && (
          <div className="mb-16 space-y-8">
            <StoryComponent />
          </div>
        )}

        {isEditing && (
          <motion.form
            id="story-form"
            onSubmit={handleSubmit}
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35, ease: [0.25, 0.1, 0.25, 1] }}
            className="space-y-6"
          >
            <section className="rounded-2xl space-y-3 mb-10">
              {/* Toolbar */}
              <div className="flex items-center justify-between gap-3 rounded-xl bg-[#2C2C2E] px-2 py-1.5 w-full">
                <div className="flex items-center gap-2">
                <button
                  type="button"
                  disabled={isLoading}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCommand('bold');
                  }}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                    activeStyles.bold
                      ? 'bg-[#F33363] text-white'
                      : 'text-[#F2F2F7] hover:bg-[#3A3A3C] active:bg-[#3A3A3C]',
                    isLoading && 'opacity-50 pointer-events-none',
                  )}
                  aria-label="Bold"
                >
                  <Bold className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCommand('italic');
                  }}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                    activeStyles.italic
                      ? 'bg-[#F33363] text-white'
                      : 'text-[#F2F2F7] hover:bg-[#3A3A3C] active:bg-[#3A3A3C]',
                    isLoading && 'opacity-50 pointer-events-none',
                  )}
                  aria-label="Italic"
                >
                  <Italic className="h-4 w-4" />
                </button>
                <button
                  type="button"
                  disabled={isLoading}
                  onMouseDown={(e) => {
                    e.preventDefault();
                    handleCommand('underline');
                  }}
                  className={cn(
                    'inline-flex h-8 w-8 items-center justify-center rounded-lg transition-colors',
                    activeStyles.underline
                      ? 'bg-[#F33363] text-white'
                      : 'text-[#F2F2F7] hover:bg-[#3A3A3C] active:bg-[#3A3A3C]',
                    isLoading && 'opacity-50 pointer-events-none',
                  )}
                  aria-label="Underline"
                >
                  <Underline className="h-4 w-4" />
                </button>
                </div>
                <button
                  type="button"
                  onClick={showAttemptsInfoToast}
                  className="text-[11px] sm:text-xs font-medium text-yellow-400 hover:text-yellow-300 transition-colors text-right"
                >
                  Plagiarism attempts left: {attemptsLeft}
                </button>
              </div>

              {/* Editor */}
              <div
                ref={editorRef}
                contentEditable={!isLoading}
                suppressContentEditableWarning
                className={cn(
                  'min-h-[200px] rounded-2xl bg-transparent border border-[#3A3A3C] px-4 py-3 text-base',
                  'focus:outline-none focus:ring-2 focus:ring-[#F33363] focus:border-transparent',
                )}
                onInput={(e) => {
                  const target = e.currentTarget as HTMLDivElement;
                  setContent(target.innerHTML);
                  checkActiveState();
                  if (error || storeError) {
                    setError(null);
                    setStoreError(null);
                  }
                }}
                onKeyUp={checkActiveState}
                onMouseUp={checkActiveState}
              />
              <p className="text-xs text-[#8E8E93]">
                Use formatting options to highlight key parts of your journey. This will appear on
                your public profile.
              </p>
              <div className="flex items-center justify-end text-xs text-[#8E8E93]">
                {storyTextLength}/{STORY_MAX_LENGTH}
              </div>
            </section>
          </motion.form>
        )}
      </div>
      <Sheet
        open={isPlagiarismDrawerOpen}
        onOpenChange={setIsPlagiarismDrawerOpen}
        title="Your content is highly plagiarized! Please rephrase it."
      >
        <div className="space-y-3 text-sm text-[#E5E5EA]">
          <p className="text-yellow-400 font-medium">
            Plagiarism score: {plagiarismScore ?? 0}%
          </p>
          <p>
            Your content matches existing sources. Please revise it to ensure originality.
          </p>
        </div>
      </Sheet>
    </div>
  );
};

